import * as React from 'react'
import styled, { css } from 'styled-components'
import { Formik, Field, FieldProps, FormikProps } from 'formik'

import { imageUploader } from '@owl-nest/behaviours'
import * as yup from '@owl-nest/validators'
import * as plume from '@ulule/owl-kit-components/next'

import { ImageUploaderField, ImageUploaderFieldProps } from './ImageUploaderField'
import { ImageListField, ListFeature } from './ImageListField'

export type { ListFeature } from './ImageListField'
export type { ImageUploaderFieldProps } from './ImageUploaderField'

type UploadFeature = {
  uploadImage: (file: File) => Promise<void>
  translations: ImageUploaderFieldProps['translations']
}

type Values = { file?: File } | { source?: string }

export type UploadImageModalProps = {
  className?: string
  open: boolean
  onDismiss: () => void
  translations: {
    submitButton: string
    serverSideError: string
  }
  features: {
    list?: ListFeature
    upload?: UploadFeature
  }
}

export function UploadImageModal({
  className,
  open,
  onDismiss,
  translations,
  features,
}: UploadImageModalProps): React.ReactElement {
  const listFeature = features.list
  const uploadFeature = features.upload

  return (
    <plume.Modal className={className} closable onClose={onDismiss} open={open}>
      {/* we don't use <form> because this Modal might live within a form and
      html5 forbids nested <form> elements */}
      {open && (
        <Formik
          initialValues={{} as Values}
          onSubmit={async (values, helper) => {
            if ('file' in values && values.file !== undefined && uploadFeature !== undefined) {
              try {
                await uploadFeature.uploadImage(values.file)
              } catch (error) {
                helper.setFieldError('file', translations.serverSideError)
              }
            }
            if ('source' in values && values.source !== undefined && listFeature !== undefined) {
              listFeature.insertImage(values.source)
            }
          }}
          validationSchema={schema}
        >
          {(formikProps: FormikProps<Values>) => {
            return (
              <>
                {uploadFeature && (
                  <Field name="file">
                    {({ field, form, meta }: FieldProps<File | undefined>) => {
                      return (
                        <StyledImageUploaderField
                          error={meta.error}
                          onChange={(file) => form.setFieldValue(field.name, file)}
                          onRemove={() => form.setFieldValue(field.name, undefined)}
                          translations={uploadFeature.translations}
                        />
                      )
                    }}
                  </Field>
                )}

                {listFeature && (
                  <Field name="source">
                    {({ field, form }: FieldProps<string | undefined>) => {
                      return (
                        <StyledImageListField
                          selected={field.value}
                          onChange={(src) => {
                            form.setFieldValue(field.name, src)
                          }}
                          onRemove={() => {
                            form.setFieldValue(field.name, undefined)
                          }}
                          feature={listFeature}
                        />
                      )
                    }}
                  </Field>
                )}

                <SubmitButton
                  kind="primary"
                  // type="button" is essential, because we don't want to accidentally
                  // submit a parent <form />
                  type="button"
                  loading={formikProps.isSubmitting}
                  onClick={formikProps.submitForm}
                  $disabled={!formikProps.dirty || !formikProps.isValid}
                >
                  {translations.submitButton}
                </SubmitButton>
              </>
            )
          }}
        </Formik>
      )}
    </plume.Modal>
  )
}

const schema = yup.lazy((value: Values) => {
  if ('file' in value) {
    return yup.object().shape({
      file: yup.file().max(imageUploader.MAX_FILE_SIZE).formats(imageUploader.ALLOWED_MIME_TYPES).required(),
    })
  } else {
    return yup.object().shape({
      source: yup.url().required(),
    })
  }
})

const StyledImageUploaderField = styled(ImageUploaderField)`
  margin: 20px 0 0;
  flex: 0 0 220px;
`

const StyledImageListField = styled(ImageListField)`
  margin: 30px 0 20px;
`

const SubmitButton = styled(plume.Button)<{ $disabled: boolean }>`
  bottom: 10px;
  display: block;
  margin: 0 auto;
  position: sticky;
  z-index: 2;

  ${({ disabled }) => {
    if (disabled) {
      return css`
        display: none;
      `
    }
  }}
`
